import { z } from 'zod';

/**
 * Grupos de permissões (v7 — DESIGN-V7 §1). As SEÇÕES são a fonte canônica:
 * cada key vive em exatamente uma seção (a da tela do TiFlux) e
 * PERMISSION_KEYS é DERIVADA delas — não existe segunda lista a manter.
 * Curada ao que o produto TEM; labels = as do TiFlux (tiflux-51/55,
 * scratchpad). Adicionar uma key = adicionar na seção + label + help — os
 * Record<PermissionKey, string> quebram o typecheck se algo ficar de fora.
 */
const SECTION_DEFS = [
  {
    id: 'administrativas',
    label: 'Permissões administrativas',
    keys: [
      'clients.view',
      'clients.manage',
      'contracts.view',
      'contracts.manage',
      'resources.view',
      'resources.manage',
      'tickets.edit_closed',
      'integrations.manage',
      'custom_fields.manage',
      'agendas.manage',
    ],
  },
  {
    id: 'gestao',
    label: 'Permissões de gestão',
    keys: [
      'permission_groups.manage',
      'users.manage',
      'users.impersonate',
      'catalogs.manage',
      'desks.manage',
      'tickets.review_reopen',
      'mail.manage',
    ],
  },
  {
    id: 'tecnicas',
    label: 'Permissões técnicas',
    keys: [
      // spec 063: as áreas da equipe de tickets que a API não barra para leitura
      // têm chave própria — quem só cuida de uma mesa (o Estoque da PoolTrack)
      // não as vê no menu
      'dashboard.view',
      'agenda.view',
      'kb.view',
      'vault.view',
      'settings.view',
      'tickets.view_open',
      'tickets.edit',
      'tickets.change_priority',
      'sla.pause_resume',
      'appointments.create_edit',
      'appointments.edit_all',
      'internal_comm.view_add',
      'kb.manage',
      'tickets.view_others',
      'chat.access',
      'chat.cancel',
      'chat.manage_others',
      'scheduled.manage',
      'vault.manage',
      'agenda.view_all',
      'pretickets.access',
      'models.manage',
      'checklists.manage',
      'service_orders.manage',
    ],
  },
  {
    id: 'relatorios',
    label: 'Permissões de relatório',
    keys: ['reports.admin', 'reports.attendants'],
  },
] as const;

export type PermissionKey = (typeof SECTION_DEFS)[number]['keys'][number];
export type PermissionSectionId = (typeof SECTION_DEFS)[number]['id'];

/** Todas as keys, na ordem das seções — derivada, nunca mantida à parte. */
export const PERMISSION_KEYS: readonly PermissionKey[] = SECTION_DEFS.flatMap((s) => s.keys);

export const PermissionKeySchema = z.enum(PERMISSION_KEYS as [PermissionKey, ...PermissionKey[]]);

export interface PermissionSection {
  id: PermissionSectionId;
  label: string;
  keys: readonly PermissionKey[];
}

/** Seções da tela de detalhe do grupo (mesma divisão do TiFlux). */
export const PERMISSION_SECTIONS: readonly PermissionSection[] = SECTION_DEFS;

/**
 * Keys que o admin tem e o resto só ganha por GRUPO (spec 020, FR-001). O
 * back-compat de "staff sem grupo faz tudo" (DESIGN-V7 §1) NÃO as alcança: ver
 * o portal como um cliente é poder novo, e ninguém o herda por omissão — sem
 * esta lista, todo atendente fora de grupo teria a permissão no dia do deploy.
 *
 * Desde 03/11 entram aqui também as keys que configuram o ACESSO e o SISTEMA. Antes, o
 * atendente fora de grupo podia editar grupos de permissão (o próprio, inclusive: a
 * restrição de mesa não valia nada), religar mesas a grupos, gerar chave da API do
 * bot, disparar campanha de WhatsApp e configurar TiFlux, e-mail e importações.
 * Medido na PoolTrack antes do corte: nenhum atendente tinha criado importação,
 * resposta rápida, campanha ou configuração; quem configura é admin.
 */
export const ADMIN_DEFAULT_ONLY_KEYS: readonly PermissionKey[] = [
  'users.impersonate',
  'permission_groups.manage',
  'users.manage',
  'desks.manage',
  'integrations.manage',
  'mail.manage',
  'catalogs.manage',
  'custom_fields.manage',
];

/** O que o staff SEM grupo algum recebe: todas as keys menos as de admin. */
export const STAFF_DEFAULT_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (key) => !ADMIN_DEFAULT_ONLY_KEYS.includes(key)
);

/**
 * Permissões efetivas de um usuário. 'all' = bypass total, só do admin. O staff
 * que não pertence a grupo algum (back-compat DESIGN-V7 §1) recebe o conjunto
 * STAFF_DEFAULT_PERMISSION_KEYS — tudo menos as keys só de admin (spec 020).
 */
export type ResolvedPermissions = ReadonlySet<string> | 'all';

/**
 * A primitiva ÚNICA de autorização (api camadas 1 e 2, web can()): 'all'
 * libera tudo; um Set libera só o que contém.
 */
export function hasPermission(resolved: ResolvedPermissions, key: PermissionKey): boolean {
  return resolved === 'all' || resolved.has(key);
}

/** Labels dos checkboxes — textos do TiFlux, na íntegra. */
export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  'clients.view': 'Visualizar clientes e grupos de recursos',
  'clients.manage': 'Gerenciar clientes e grupos de recursos',
  'contracts.view': 'Visualizar contratos',
  'contracts.manage': 'Gerenciar contratos',
  'resources.view': 'Visualizar recursos',
  'resources.manage': 'Gerenciar recursos',
  'tickets.edit_closed': 'Editar tickets fechados',
  'integrations.manage': 'Gerenciar integrações',
  'custom_fields.manage': 'Gerenciar campos personalizados',
  'agendas.manage': 'Gerenciar agendas',
  'permission_groups.manage': 'Gerenciar grupos de permissão',
  'users.manage': 'Gerenciar usuários',
  'users.impersonate': 'Ver como cliente (sessão de depuração)',
  'catalogs.manage': 'Gerenciar catálogos de serviço',
  'desks.manage': 'Gerenciar mesas de serviços',
  'tickets.review_reopen': 'Revisar e reabrir tickets fechados',
  'mail.manage': 'Gerenciar tickets por e-mail',
  'tickets.view_open': 'Visualizar e abrir tickets',
  'tickets.edit': 'Editar tickets',
  'tickets.change_priority': 'Mudar a prioridade e desativar SLA dos tickets',
  'sla.pause_resume': 'Pausar e retomar SLA',
  'appointments.create_edit': 'Criar e editar apontamentos',
  'appointments.edit_all': 'Editar apontamentos de todos',
  'internal_comm.view_add': 'Visualizar e adicionar mensagens na comunicação interna',
  'dashboard.view': 'Ver o Dashboard, o Painel e os Apontamentos',
  'agenda.view': 'Ver a agenda',
  'kb.view': 'Consultar a base de conhecimento',
  'vault.view': 'Usar o cofre de senhas',
  'settings.view': 'Ver as configurações',
  'kb.manage': 'Gerenciar base de conhecimento',
  'tickets.view_others': 'Visualizar tickets em que eu não sou o responsável',
  'chat.access': 'Acessar o chat',
  'chat.cancel': 'Cancelar chats',
  'chat.manage_others': 'Gerenciar conversas de outros',
  'scheduled.manage': 'Criar agendamento de tickets',
  'vault.manage': 'Gerenciar cofre de senhas',
  'agenda.view_all': 'Visualizar todas as agendas',
  'pretickets.access': 'Acessar pré-tickets',
  'models.manage': 'Gerenciar modelos',
  'checklists.manage': 'Gerenciar checklists',
  'service_orders.manage': 'Gerenciar ordens de serviço',
  'reports.admin': 'Visualizar relatórios administrativos',
  'reports.attendants': 'Visualizar relatórios dos atendentes',
};

/** Uma frase por permissão — tooltip do ícone ? na tela do grupo. */
export const PERMISSION_HELP: Record<PermissionKey, string> = {
  'clients.view': 'Ver a lista e o detalhe dos clientes e seus contatos.',
  'clients.manage': 'Criar, editar e excluir clientes, contatos e endereços.',
  'contracts.view': 'Ver contratos e faturas dos clientes.',
  'contracts.manage': 'Criar e editar contratos, tipos de contrato e faturas.',
  'resources.view': 'Ver os recursos (equipamentos) cadastrados.',
  'resources.manage': 'Criar, editar e excluir recursos, tipos e grupos de recursos.',
  'tickets.edit_closed': 'Alterar os campos permitidos de tickets já fechados.',
  'integrations.manage': 'Configurar integrações externas, como o WhatsApp.',
  'custom_fields.manage': 'Criar e editar campos personalizados de ticket, recurso e cliente.',
  'agendas.manage': 'Gerenciar calendários da agenda, inclusive os de outros atendentes.',
  'permission_groups.manage': 'Criar e editar grupos de permissões e seus vínculos.',
  'users.manage': 'Criar e editar usuários e grupos de atendentes.',
  'users.impersonate':
    'Abrir o portal com a identidade de um usuário do cliente, só leitura, por até 60 minutos e com motivo registrado. Só o admin tem por padrão.',
  'catalogs.manage': 'Criar e editar catálogos de serviço, áreas e itens.',
  'desks.manage': 'Criar e configurar mesas de serviço, seus estágios, status e automações.',
  'tickets.review_reopen': 'Reabrir tickets que já foram fechados.',
  'mail.manage': 'Configurar o recebimento e os modelos de e-mail dos tickets.',
  'tickets.view_open': 'Ver a lista de tickets e abrir tickets novos.',
  'tickets.edit': 'Editar os campos de um ticket aberto.',
  'tickets.change_priority': 'Alterar a prioridade de um ticket.',
  'sla.pause_resume': 'Pausar e retomar a contagem de SLA de um ticket.',
  'appointments.create_edit': 'Criar e editar os próprios apontamentos de horas nos tickets.',
  'appointments.edit_all': 'Editar e excluir apontamentos feitos por qualquer atendente.',
  'internal_comm.view_add': 'Ver e escrever comentários internos nos tickets.',
  'dashboard.view': 'Abrir o Dashboard (layout Completo), o Painel e a lista de Apontamentos (layout Simplificado).',
  'agenda.view': 'Abrir a Agenda (a própria; a de todos pede "Visualizar todas as agendas").',
  'kb.view': 'Abrir os Conhecimentos e as Novidades.',
  'vault.view': 'Abrir o Cofre de senhas (cada cofre segue as permissões do próprio cofre).',
  'settings.view': 'Abrir as Configurações (as telas de gestão seguem pedindo a permissão de cada uma).',
  'kb.manage': 'Criar e editar pastas e artigos da base de conhecimento.',
  'tickets.view_others': 'Ver tickets cujo responsável é outro atendente.',
  'chat.access': 'Acessar e atender as conversas de chat.',
  'chat.cancel': 'Cancelar uma conversa de chat em andamento.',
  'chat.manage_others': 'Assumir e encerrar conversas que estão com outro atendente.',
  'scheduled.manage': 'Criar e editar atividades agendadas que abrem tickets automaticamente.',
  'vault.manage': 'Criar cofres de senhas novos (o acesso a cada cofre segue as permissões do próprio cofre).',
  'agenda.view_all': 'Ver a agenda de todos os atendentes, não só a própria.',
  'pretickets.access': 'Ver e converter pré-tickets em tickets.',
  'models.manage': 'Criar e editar modelos de resposta e de apontamento.',
  'checklists.manage': 'Criar e editar modelos de checklist.',
  'service_orders.manage':
    'Gerar, agendar e encerrar ordens de serviço dos tickets (o cadastro de técnicos é do admin).',
  'reports.admin': 'Ver os relatórios executivo, de catálogo e de avaliações.',
  'reports.attendants': 'Ver os relatórios de extrato de apontamentos, carga de trabalho e picos.',
};

/** Grupo como sai da API (GET /permission-groups[/:id]). */
export const PermissionGroupSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(z.string()),
  userIds: z.array(z.number().int()),
  attendantGroupIds: z.array(z.number().int()),
  /** spec 058: as mesas que o grupo enxerga; null = todas */
  deskIds: z.array(z.number().int()).nullable(),
});
export type PermissionGroup = z.infer<typeof PermissionGroupSchema>;

/** POST cria; PUT substitui permissões e vínculos pelas listas enviadas. */
export const PermissionGroupBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().nullish(),
  permissions: z.array(PermissionKeySchema).default([]),
  userIds: z.array(z.number().int().positive()).default([]),
  attendantGroupIds: z.array(z.number().int().positive()).default([]),
  /**
   * spec 058: quem está SÓ em grupos com mesas enxerga só a união delas (tickets,
   * conversas e o tempo real). null ou vazia = todas, como sempre foi.
   */
  deskIds: z.array(z.number().int().positive()).nullable().default(null),
});
export type PermissionGroupBody = z.infer<typeof PermissionGroupBodySchema>;
